import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserInfo, CompanyInfo } from '../type'
/**
 * 인증 스토어
 */
export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>('')
  const refreshToken = ref<string>('')
  const currentUser = ref<UserInfo | null>(null)
  const expiresAt = ref<string | null>(null)
  const refreshExpiresAt = ref<string | null>(null)

  const userId = computed<number | null>(() => currentUser.value?.id ?? null)
  const userName = computed<string | null>(() => currentUser.value?.name ?? null)
  const loginId = computed<string | null>(() => currentUser.value?.login_id ?? null)
  const userEmail = computed<string | null>(() => currentUser.value?.email ?? null)
  const company = computed<CompanyInfo | null>(() => currentUser.value?.company ?? null)
  const companyId = computed<number | null>(() => currentUser.value?.company_id ?? null)
  const companyName = computed<string | null>(() => currentUser.value?.company?.name ?? null)

  const isLoggedIn = computed<boolean>(() => !!token.value && !!currentUser.value)
  const isTokenExpired = computed<boolean>(() => {
    if (!expiresAt.value) return true
    return new Date(expiresAt.value) < new Date()
  })

  // 리프레시토큰 만료 확인
  const isRefreshTokenExpired = computed<boolean>(() => {
    if (!refreshExpiresAt.value) return true
    return new Date(refreshExpiresAt.value) < new Date()
  })

  /**
   * 인증 데이터 설정
   * @param {string} newToken - 액세스 토큰
   * @param {UserInfo} user - 사용자 정보
   * @param {string} expires - 액세스 토큰 만료 시간
   * @param {string} newRefreshToken - 리프레시 토큰 (선택)
   * @param {string} refreshExpires - 리프레시 토큰 만료 시간 (선택)
   * @returns {void}
   */
  function setAuthData(
    newToken: string,
    user: UserInfo,
    expires: string,
    newRefreshToken?: string,
    refreshExpires?: string,
  ): void {
    token.value = newToken
    currentUser.value = user
    expiresAt.value = expires
    if (newRefreshToken) refreshToken.value = newRefreshToken
    if (refreshExpires) refreshExpiresAt.value = refreshExpires
  }

  /**
   * 사용자 정보 업데이트
   * @param {UserInfo} user - 사용자 정보
   * @returns {void}
   */
  function updateUser(user: UserInfo): void {
    currentUser.value = user
  }

  /**
   * 액세스 토큰 업데이트
   * @param {string} newToken - 액세스 토큰
   * @returns {void}
   */
  function updateToken(newToken: string): void {
    token.value = newToken
  }

  /**
   * 토큰 만료 시간 업데이트
   * @param {string} expires - 만료 시간
   * @returns {void}
   */
  function updateExpiresAt(expires: string): void {
    expiresAt.value = expires
  }

  function clearAuthData(): void {
    token.value = ''
    refreshToken.value = ''
    currentUser.value = null
    expiresAt.value = null
    refreshExpiresAt.value = null
  }

  function getToken(): string {
    return token.value
  }

  function getRefreshToken(): string {
    return refreshToken.value
  }

  function getUser(): UserInfo | null {
    return currentUser.value
  }

  return {
    token,
    refreshToken,
    currentUser,
    expiresAt,
    refreshExpiresAt,
    userId,
    userName,
    loginId,
    userEmail,
    company,
    companyId,
    companyName,
    isLoggedIn,
    isTokenExpired,
    isRefreshTokenExpired,
    setAuthData,
    updateUser,
    updateToken,
    updateExpiresAt,
    clearAuthData,
    getToken,
    getRefreshToken,
    getUser,
  }
})
